import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Plus, Pencil, Trash2, Receipt, Loader2, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';

const PARA_BIRIMLERI = ['TRY', 'USD', 'EUR', 'GBP'];
const TUTAR_ISLECLERI = [
  { deger: '=', etiket: '=' }, { deger: '>', etiket: '>' }, { deger: '>=', etiket: '≥' },
  { deger: '<', etiket: '<' }, { deger: '<=', etiket: '≤' }, { deger: 'between', etiket: 'aralık' },
];
// Boş filtre TEK yerde tanımlı: "temizle" ile ilk yükleme aynı duruma dönsün.
const BOS_SUTUN = {
  invoice_number: '', invoice_type: '', customer_search: '', date_from: '', date_to: '',
  due_from: '', due_to: '', amount_op: '', amount: '', amount2: '',
  currency: '', status: '',
};

const paraBicim = (n) => (Number(n) || 0).toLocaleString('tr-TR',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const trTarih = (d) => (d ? new Date(d).toLocaleDateString('tr-TR') : '—');
// "1.234,56" → 1234.56 (kullanıcı virgüllü yazabilmeli)
const sayiyaCevir = (m) => {
  const t = String(m ?? '').replace(/\./g, '').replace(',', '.').trim();
  const v = parseFloat(t);
  return Number.isFinite(v) ? v : undefined;
};

const TUR_ETIKET = {
  fatura: 'Fatura', yazar_kasa: 'Yazar Kasa Fişi',
  faturasiz: 'Faturasız Satış', iade: 'Firma İade Faturası',
};

const SUTUNLAR = [
  { anahtar: 'invoice_number', etiket: 'Fatura No', siralanabilir: true },
  { anahtar: 'tur', etiket: 'Tür' },
  { anahtar: 'customer', etiket: 'Müşteri', siralanabilir: true },
  { anahtar: 'invoice_date', etiket: 'Tarih', siralanabilir: true },
  { anahtar: 'due_date', etiket: 'Vade', siralanabilir: true },
  { anahtar: 'amount', etiket: 'Tutar', siralanabilir: true, sag: true },
  { anahtar: 'durum', etiket: 'Durum' },
  { anahtar: 'islem', etiket: '', sag: true },
];

export default function SatisFaturaList() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const duzenleyebilir = hasPermission('satis_faturalari', 'edit');
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const silmeYetkisi = hasPermission('satis_faturalari', 'delete');

  const [kayitlar, setKayitlar] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [sayfa, setSayfa] = useState(1);
  const [perPage] = useState(50);
  const [sira, setSira] = useState({ by: 'invoice_date', dir: 'desc' });
  // `taslak` kutulara yazılanı tutar, `uygulanan` isteğe gider: her tuş
  // vuruşunda sunucuya gitmek listeyi titretiyordu (faturalar ekranındaki
  // desenin aynısı).
  const [taslak, setTaslak] = useState(BOS_SUTUN);
  const [uygulanan, setUygulanan] = useState(BOS_SUTUN);
  const zamanlayici = useRef(null);

  const sutunDegistir = (alan, deger, anlik = false) => {
    setTaslak(p => ({ ...p, [alan]: deger }));
    clearTimeout(zamanlayici.current);
    const uygula = () => {
      setSayfa(1);
      setUygulanan(p => ({ ...p, [alan]: deger }));
    };
    if (anlik) uygula();
    else zamanlayici.current = setTimeout(uygula, 400);
  };

  const temizle = () => {
    clearTimeout(zamanlayici.current);
    setTaslak(BOS_SUTUN);
    setUygulanan(BOS_SUTUN);
    setSayfa(1);
  };

  const getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      const p = { page: sayfa, per_page: perPage, sort_by: sira.by, sort_dir: sira.dir };
      Object.entries(uygulanan).forEach(([k, v]) => {
        if (v === '' || v == null) return;
        if (k === 'amount' || k === 'amount2') {
          const s = sayiyaCevir(v);
          if (s !== undefined) p[k] = s;
        } else p[k] = v;
      });
      const r = await api.get('/satis-faturalari', { params: p });
      setKayitlar(r.data.items || []);
      setToplam(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Satış faturaları yüklenemedi');
    } finally {
      setYukleniyor(false);
    }
  }, [sayfa, perPage, sira, uygulanan]);

  useEffect(() => { getir(); }, [getir]);

  const sirala = (anahtar) => {
    setSayfa(1);
    setSira(p => (p.by === anahtar
      ? { by: anahtar, dir: p.dir === 'asc' ? 'desc' : 'asc' }
      : { by: anahtar, dir: 'desc' }));
  };

  const sil = async (f) => {
    try {
      const { data } = await api.get(`/satis-faturalari/${f.id}/silme-onizleme`);
      const mesaj = `${data.fatura_no} numaralı satış faturası silinecek.\n\n`
        + `Tutar: ${paraBicim(data.tutar)} ${data.para_birimi}\n`
        + `${data.stok_hareketi} stok hareketi İADE edilecek\n`
        + `${data.cari_kaydi} cari kaydı silinecek\n\nDevam edilsin mi?`;
      if (!window.confirm(mesaj)) return;
      await api.delete(`/satis-faturalari/${f.id}`);
      toast.success('Satış faturası silindi');
      getir();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const sonSayfa = Math.max(1, Math.ceil(toplam / perPage));
  const filtreVar = Object.values(uygulanan).some(v => v !== '' && v != null);

  return (
    <div data-testid="satis-fatura-listesi">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight flex items-center gap-2">
            <Receipt size={26} /> Satış Faturaları
          </h1>
          <p className="text-xs text-[#8a7e70] mt-0.5">
            Satış belgeleri — karşı cari hesabın bakiyesini artı yönde hareket
            ettirir, stoktan düşer
          </p>
        </div>
        <div className="flex items-center gap-2">
          {filtreVar && (
            <Button variant="outline" size="sm" onClick={temizle}
              className="border-[#d0c4b0]" data-testid="satis-filtre-temizle">Filtreyi Temizle</Button>
          )}
          {duzenleyebilir && (
            <Button onClick={() => navigate('/erp/satis-faturalari/yeni')}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"
              data-testid="satis-yeni">
              <Plus size={16} className="mr-1.5" /> Yeni Satış Faturası
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  {SUTUNLAR.map(s => (
                    <TableHead key={s.anahtar}
                      className={`text-[#1a1916] font-bold text-xs uppercase ${s.sag ? 'text-right' : ''}`}>
                      {s.siralanabilir ? (
                        <button type="button" onClick={() => sirala(s.anahtar)}
                          className="inline-flex items-center gap-1 hover:text-[#3d6e4e]"
                          data-testid={`satis-sirala-${s.anahtar}`}>
                          {s.etiket}
                          {sira.by === s.anahtar
                            ? (sira.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                            : <ArrowUpDown size={12} className="opacity-40" />}
                        </button>
                      ) : s.etiket}
                    </TableHead>
                  ))}
                </TableRow>
                {/* SÜZME SUNUCUDA: liste sayfalı, istemcide süzmek yalnızca
                    açık sayfayı süzer ve öbür sayfalardaki eşleşmeleri gizler. */}
                <TableRow className="bg-[#f5f0e8] hover:bg-[#f5f0e8]">
                  <TableHead className="py-1.5">
                    <Input value={taslak.invoice_number} placeholder="No ara"
                      onChange={(e) => sutunDegistir('invoice_number', e.target.value)}
                      className="h-7 text-xs bg-white border-[#d0c4b0]" data-testid="satis-f-no" />
                  </TableHead>
                  <TableHead className="py-1.5">
                    <Select value={taslak.invoice_type || undefined}
                      onValueChange={(v) => sutunDegistir('invoice_type', v === 'hepsi' ? '' : v, true)}>
                      <SelectTrigger className="h-7 text-xs bg-white border-[#d0c4b0]"
                        data-testid="satis-f-tur"><SelectValue placeholder="Hepsi" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hepsi">Hepsi</SelectItem>
                        {Object.entries(TUR_ETIKET).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableHead>
                  <TableHead className="py-1.5">
                    <Input value={taslak.customer_search} placeholder="Müşteri ara"
                      onChange={(e) => sutunDegistir('customer_search', e.target.value)}
                      className="h-7 text-xs bg-white border-[#d0c4b0]" data-testid="satis-f-musteri" />
                  </TableHead>
                  <TableHead className="py-1.5">
                    <div className="flex gap-1">
                      <Input type="date" value={taslak.date_from}
                        onChange={(e) => sutunDegistir('date_from', e.target.value, true)}
                        className="h-7 text-xs bg-white border-[#d0c4b0] px-1" data-testid="satis-f-tarih1" />
                      <Input type="date" value={taslak.date_to}
                        onChange={(e) => sutunDegistir('date_to', e.target.value, true)}
                        className="h-7 text-xs bg-white border-[#d0c4b0] px-1" data-testid="satis-f-tarih2" />
                    </div>
                  </TableHead>
                  <TableHead className="py-1.5">
                    <div className="flex gap-1">
                      <Input type="date" value={taslak.due_from}
                        onChange={(e) => sutunDegistir('due_from', e.target.value, true)}
                        className="h-7 text-xs bg-white border-[#d0c4b0] px-1" data-testid="satis-f-vade1" />
                      <Input type="date" value={taslak.due_to}
                        onChange={(e) => sutunDegistir('due_to', e.target.value, true)}
                        className="h-7 text-xs bg-white border-[#d0c4b0] px-1" data-testid="satis-f-vade2" />
                    </div>
                  </TableHead>
                  <TableHead className="py-1.5">
                    <div className="flex gap-1 justify-end">
                      <Select value={taslak.amount_op || undefined}
                        onValueChange={(v) => sutunDegistir('amount_op', v, true)}>
                        <SelectTrigger className="h-7 w-16 text-xs bg-white border-[#d0c4b0] px-1"
                          data-testid="satis-f-tutar-op"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {TUTAR_ISLECLERI.map(o => <SelectItem key={o.deger} value={o.deger}>{o.etiket}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={taslak.amount} placeholder="tutar"
                        onChange={(e) => sutunDegistir('amount', e.target.value)}
                        className="h-7 w-20 text-xs bg-white border-[#d0c4b0] px-1" data-testid="satis-f-tutar" />
                      {taslak.amount_op === 'between' && (
                        <Input value={taslak.amount2} placeholder="—"
                          onChange={(e) => sutunDegistir('amount2', e.target.value)}
                          className="h-7 w-20 text-xs bg-white border-[#d0c4b0] px-1" data-testid="satis-f-tutar2" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="py-1.5">
                    <Select value={taslak.status || undefined}
                      onValueChange={(v) => sutunDegistir('status', v === 'hepsi' ? '' : v, true)}>
                      <SelectTrigger className="h-7 text-xs bg-white border-[#d0c4b0]"
                        data-testid="satis-f-durum"><SelectValue placeholder="Hepsi" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hepsi">Hepsi</SelectItem>
                        <SelectItem value="completed">Kesinleşti</SelectItem>
                        <SelectItem value="draft">Taslak</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableHead>
                  <TableHead className="py-1.5">
                    <Select value={taslak.currency || undefined}
                      onValueChange={(v) => sutunDegistir('currency', v === 'hepsi' ? '' : v, true)}>
                      <SelectTrigger className="h-7 text-xs bg-white border-[#d0c4b0]"
                        data-testid="satis-f-pb"><SelectValue placeholder="PB" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hepsi">Hepsi</SelectItem>
                        {PARA_BIRIMLERI.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {yukleniyor ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12">
                    <Loader2 className="animate-spin mx-auto" size={28} />
                  </TableCell></TableRow>
                ) : kayitlar.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-[#8a7e70]">
                    {filtreVar ? 'Filtreye uyan satış faturası yok'
                      : 'Henüz satış faturası yok — "Yeni Satış Faturası" ile başlayın'}
                  </TableCell></TableRow>
                ) : kayitlar.map(f => (
                  <TableRow key={f.id} className="erp-table-row border-[#d0c4b0]">
                    <TableCell className="font-mono text-sm text-[#1a1916]">{f.invoice_number || '—'}</TableCell>
                    <TableCell className="text-xs text-[#3F3E39] whitespace-nowrap">
                      {/* Iade ayri renkte: ayni listede satis ve iade birlikte
                          duruyor, gozle ayirt edilebilmeli. */}
                      <span className={f.invoice_type === 'iade'
                        ? 'px-1.5 py-0.5 rounded bg-[#7a6a2e]/10 text-[#7a6a2e] font-semibold'
                        : ''}>
                        {TUR_ETIKET[f.invoice_type] || TUR_ETIKET.fatura}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-[#1a1916]">{f.customer_name || '—'}</TableCell>
                    <TableCell className="text-sm text-[#3F3E39]">{trTarih(f.invoice_date)}</TableCell>
                    <TableCell className="text-sm text-[#3F3E39]">{trTarih(f.due_date)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-[#1a1916]">
                      {paraBicim(f.amount_foreign)}
                      <span className="text-[10px] font-sans text-[#8a7e70] ml-1">{f.currency}</span>
                      {f.currency !== 'TRY' && (
                        <div className="text-[10px] font-sans text-[#8a7e70]">
                          {paraBicim(f.amount_try)} TL
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge ${f.status === 'completed'
                        ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#7a6a2e]/10 text-[#7a6a2e]'}`}>
                        {f.status === 'completed' ? 'Kesinleşti' : 'Taslak'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {duzenleyebilir && (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost"
                            onClick={() => navigate(`/erp/satis-faturalari/${f.id}/duzenle`)}
                            className="text-[#3F3E39] hover:bg-[#e8dfd0]"
                            data-testid={`satis-duzenle-${f.id}`}><Pencil size={14} /></Button>
                          {silmeYetkisi && (
                          <Button size="sm" variant="ghost" onClick={() => sil(f)}
                            className="text-[#7a3535] hover:bg-[#7a3535]/10"
                            data-testid={`satis-sil-${f.id}`}><Trash2 size={14} /></Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between border-t border-[#d0c4b0] px-4 py-2 text-sm">
            <span className="text-[#8a7e70]">{toplam} fatura</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={sayfa <= 1}
                onClick={() => setSayfa(p => Math.max(1, p - 1))}
                className="border-[#d0c4b0] h-7 px-2"><ChevronLeft size={14} /></Button>
              <span className="text-[#3F3E39]">{sayfa} / {sonSayfa}</span>
              <Button variant="outline" size="sm" disabled={sayfa >= sonSayfa}
                onClick={() => setSayfa(p => p + 1)}
                className="border-[#d0c4b0] h-7 px-2"><ChevronRight size={14} /></Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
